// =======================================================
// 2) RESTAURANT — Final SEO Editor (Ant Design)
// File: src/views/pages/restaurant/admin/SeoSection.jsx (adjust path as per your project)
// Saves EXACT SAME canonical SEO schema as hotel.
// =======================================================

import React, { useMemo } from "react";
import { Card, Form, Input, Typography, Select, Row, Col, Divider } from "antd";

const { Text } = Typography;

const ROBOTS_OPTIONS = [
  { value: "index,follow", label: "Index & Follow (default)" },
  { value: "noindex,nofollow", label: "No Index & No Follow" },
  { value: "index,nofollow", label: "Index but No Follow" },
  { value: "noindex,follow", label: "No Index but Follow" },
];

const TWITTER_CARDS = [
  { value: "summary_large_image", label: "summary_large_image" },
  { value: "summary", label: "summary" },
];

const sanitizeSlug = (v = "") =>
  String(v)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")        // ✅ spaces -> hyphen
    .replace(/[^a-z0-9-]/g, "")  // remove remaining invalid chars
    .replace(/-+/g, "-")         // collapse multiple hyphens
    .replace(/^-|-$/g, "");      // trim leading/trailing hyphens


const isValidHttpUrl = (v = "") => /^https?:\/\/.+/i.test(String(v).trim());

function normalizeSeo(seoRaw = {}, defaults = {}) {
  const seo = seoRaw && typeof seoRaw === "object" ? seoRaw : {};
  const slug = sanitizeSlug(seo.slug || defaults.slug || "");

  const title = (seo.title || seo.page_title || "").trim();
  const description = (seo.description || seo.meta_description || "").trim();
  const keywords = (seo.keywords || "").trim();

  const canonicalCandidate = (seo.canonical || "").trim();
  const canonical =
    canonicalCandidate && isValidHttpUrl(canonicalCandidate)
      ? canonicalCandidate
      : (defaults.canonical || "");

  const robots = (seo.robots || "index,follow").trim();

  const ogTitle = (seo.og?.title || seo.og_title || "").trim();
  const ogDescription = (seo.og?.description || seo.og_description || "").trim();
  const ogImage = (seo.og?.image || seo.og_image || seo.ogImage || "").trim();
  const ogSiteName = (seo.og?.site_name || seo.og_site_name || "NetShine ONE").trim();
  const ogType = (seo.og?.type || "website").trim();

  const twitterCard = (seo.twitter?.card || seo.twitter_card || "summary_large_image").trim();
  const twitterTitle = (seo.twitter?.title || seo.twitter_title || "").trim();
  const twitterDescription = (seo.twitter?.description || seo.twitter_description || "").trim();
  const twitterImage = (seo.twitter?.image || seo.twitter_image || "").trim();

  const ga4 = (seo.analytics?.ga4 || seo.google_analytics_id || "").trim();
  const gsc = (seo.analytics?.google_site_verification || seo.google_site_verification || "").trim();

  const schemaInput = seo.schema_input || seo.structured_data || {};
  const priceRange = (schemaInput?.price_range || "").trim();
  const starRating =
    schemaInput?.star_rating === null || schemaInput?.star_rating === undefined
      ? null
      : Number(schemaInput.star_rating);
  const reviewCount =
    schemaInput?.review_count === null || schemaInput?.review_count === undefined
      ? null
      : Number(schemaInput.review_count);

  const favicon = (seo.assets?.favicon || seo.favicon || "").trim();

  return {
    slug,
    title,
    description,
    keywords,
    canonical,
    robots,

    og: {
      title: ogTitle,
      description: ogDescription,
      image: ogImage,
      site_name: ogSiteName,
      type: ogType,
    },

    twitter: {
      card: twitterCard,
      title: twitterTitle,
      description: twitterDescription,
      image: twitterImage,
    },

    analytics: {
      ga4,
      google_site_verification: gsc,
    },

    schema_input: {
      price_range: priceRange,
      star_rating: Number.isFinite(starRating) ? starRating : null,
      review_count: Number.isFinite(reviewCount) ? reviewCount : null,
    },

    assets: {
      favicon,
    },
  };
}

export default function SeoSection({ config, onChange }) {
  const seo = useMemo(() => {
    const slug = sanitizeSlug(config?.seo?.slug || "");
    const canonical = slug ? `https://${slug}.netshine.one/` : "";
    return normalizeSeo(config?.seo || {}, { slug, canonical });
  }, [config?.seo]);

  const patchSeo = (patch) => {
    const slug = sanitizeSlug((patch?.slug ?? seo.slug) || "");
    const canonicalDefault = slug ? `https://${slug}.netshine.one/` : "";
    const merged = normalizeSeo({ ...seo, ...patch }, { slug, canonical: canonicalDefault });
    onChange({ ...config, seo: merged });
  };

  const patchNested = (key, patch) => patchSeo({ [key]: { ...(seo?.[key] || {}), ...(patch || {}) } });

  const canonicalWarn = seo.canonical && !isValidHttpUrl(seo.canonical)
    ? "Canonical must be a full URL (https://...)."
    : "";

  const autoCanonical = () => {
    if (!seo.slug) return;
    patchSeo({ canonical: `https://${seo.slug}.netshine.one/` });
  };

  const autoSocialFromTitle = () => {
    patchNested("og", { title: seo.title || "", description: seo.description || "" });
    patchNested("twitter", { title: seo.title || "", description: seo.description || "" });
  };

  return (
    <Card bordered={false}>
      <Form layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Booking Page Slug">
              <Input
                value={seo.slug}
                onChange={(e) => patchSeo({ slug: sanitizeSlug(e.target.value) })}
                placeholder="e.g. divine-restaurant"
              />
              <Text type="secondary">https://{seo.slug || "your-restaurant"}.netshine.one/</Text>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label="Robots">
              <Select
                value={seo.robots || "index,follow"}
                onChange={(val) => patchSeo({ robots: val })}
                options={ROBOTS_OPTIONS}
              />
              <Text type="secondary">Use noindex for staging/testing.</Text>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Page Title">
          <Input
            value={seo.title}
            onChange={(e) => patchSeo({ title: e.target.value })}
            maxLength={60}
            placeholder="e.g. Divine Restaurant | Reserve a Table"
          />
          <Text type="secondary">Recommended 50–60 characters.</Text>
        </Form.Item>

        <Form.Item label="Meta Description">
          <Input.TextArea
            rows={3}
            value={seo.description}
            onChange={(e) => patchSeo({ description: e.target.value })}
            maxLength={160}
            placeholder="Reserve a table with instant confirmation and exclusive offers."
          />
          <Text type="secondary">Recommended 120–160 characters.</Text>
        </Form.Item>

        <Form.Item label="Keywords (optional)">
          <Input
            value={seo.keywords}
            onChange={(e) => patchSeo({ keywords: e.target.value })}
            placeholder="restaurant reservation, table booking, fine dining"
          />
        </Form.Item>

        <Divider />

        <Row gutter={16}>
          <Col xs={24} md={18}>
            <Form.Item label="Canonical URL (optional)">
              <Input
                value={seo.canonical}
                onChange={(e) => patchSeo({ canonical: e.target.value })}
                placeholder={seo.slug ? `https://${seo.slug}.netshine.one/` : "https://your-slug.netshine.one/"}
              />
              {canonicalWarn ? (
                <Text type="warning">{canonicalWarn}</Text>
              ) : (
                <Text type="secondary">If blank, backend can auto-fill from slug.</Text>
              )}
            </Form.Item>
          </Col>
          <Col xs={24} md={6} style={{ display: "flex", alignItems: "end" }}>
            <button
              type="button"
              onClick={autoCanonical}
              disabled={!seo.slug}
              style={{
                width: "100%",
                height: 40,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,.12)",
                background: "white",
                cursor: seo.slug ? "pointer" : "not-allowed",
                fontWeight: 700,
              }}
            >
              Auto-fill
            </button>
          </Col>
        </Row>

        <Divider />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontWeight: 700 }}>Social Preview (Open Graph / Twitter)</div>
          <button
            type="button"
            onClick={autoSocialFromTitle}
            style={{
              borderRadius: 10,
              border: "1px solid rgba(0,0,0,.12)",
              background: "white",
              cursor: "pointer",
              padding: "6px 10px",
              fontWeight: 700,
            }}
          >
            Use Title/Description
          </button>
        </div>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="OG Title (optional)">
              <Input
                value={seo.og?.title}
                onChange={(e) => patchNested("og", { title: e.target.value })}
                maxLength={70}
                placeholder="Defaults to Page Title"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="OG Image URL">
              <Input
                value={seo.og?.image}
                onChange={(e) => patchNested("og", { image: e.target.value.trim() })}
                placeholder="https://cdn.../og.jpg"
              />
              <Text type="secondary">Recommended 1200×630 for WhatsApp/social previews.</Text>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="OG Description (optional)">
          <Input.TextArea
            rows={2}
            value={seo.og?.description}
            onChange={(e) => patchNested("og", { description: e.target.value })}
            maxLength={200}
            placeholder="Defaults to Meta Description"
          />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item label="Twitter Card">
              <Select
                value={seo.twitter?.card || "summary_large_image"}
                onChange={(val) => patchNested("twitter", { card: val })}
                options={TWITTER_CARDS}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Twitter Title (optional)">
              <Input
                value={seo.twitter?.title}
                onChange={(e) => patchNested("twitter", { title: e.target.value })}
                maxLength={70}
                placeholder="Defaults to OG/Page title"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Twitter Image (optional)">
              <Input
                value={seo.twitter?.image}
                onChange={(e) => patchNested("twitter", { image: e.target.value.trim() })}
                placeholder="Defaults to OG image"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Google Analytics (GA4 Measurement ID)">
              <Input
                value={seo.analytics?.ga4}
                onChange={(e) => patchNested("analytics", { ga4: e.target.value.trim() })}
                placeholder="G-XXXXXXXXXX"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Google Site Verification (optional)">
              <Input
                value={seo.analytics?.google_site_verification}
                onChange={(e) => patchNested("analytics", { google_site_verification: e.target.value.trim() })}
                placeholder="abcdef12345..."
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Favicon URL (optional)">
              <Input
                value={seo.assets?.favicon}
                onChange={(e) => patchNested("assets", { favicon: e.target.value.trim() })}
                placeholder="https://cdn.../favicon.png"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <div style={{ fontWeight: 700, marginBottom: 8 }}>
          Structured Data Inputs (backend converts to JSON-LD)
        </div>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item label="Star Rating (1–5)">
              <Input
                type="number"
                min={1}
                max={5}
                step={0.1}
                value={seo.schema_input?.star_rating ?? ""}
                onChange={(e) =>
                  patchNested("schema_input", {
                    star_rating: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Review Count">
              <Input
                type="number"
                min={0}
                value={seo.schema_input?.review_count ?? ""}
                onChange={(e) =>
                  patchNested("schema_input", {
                    review_count: e.target.value === "" ? null : parseInt(e.target.value, 10),
                  })
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Price Range">
              <Select
                value={seo.schema_input?.price_range || ""}
                onChange={(val) => patchNested("schema_input", { price_range: val })}
                options={[
                  { value: "", label: "Select" },
                  { value: "$", label: "$ (Budget)" },
                  { value: "$$", label: "$$ (Moderate)" },
                  { value: "$$$", label: "$$$ (Luxury)" },
                  { value: "$$$$", label: "$$$$ (Ultra Luxury)" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <div style={{ padding: 12, border: "1px solid rgba(0,0,0,.06)", borderRadius: 12, background: "#fafafa" }}>
          <div style={{ fontWeight: 800, marginBottom: 4 }}>Preview</div>
          <div style={{ fontWeight: 700 }}>{seo.title || "(no title yet)"}</div>
          <div style={{ color: "rgba(0,0,0,.55)" }}>{seo.canonical || (seo.slug ? `https://${seo.slug}.netshine.one/` : "(no canonical)")}</div>
          <div style={{ marginTop: 8 }}>{seo.description || "(no description yet)"}</div>
        </div>
      </Form>
    </Card>
  );
}
